/**
 * EU Event Routing Configuration
 *
 * Events listed here will route API/WebSocket calls to EU (eu-central-1)
 * endpoints instead of the default ca-central-1 endpoints.
 *
 * To add a new EU event:
 *   1. Add the event identifier (lowercase) to EU_EVENT_IDS
 *   2. Update EU_DEV_ENDPOINTS / EU_PROD_ENDPOINTS if the API Gateway IDs change
 */

export interface EuEndpoints {
  apiUrl: string;
  wsUrl: string;
  xApiKey: string;
}

/**
 * Event identifiers that should route to EU services.
 * Use lowercase hostname prefixes (e.g., 'ata2026' for ata2026.synopsis.rozie.ai)
 */
export const EU_EVENT_IDS: Set<string> = new Set(['wcs2026']);

/** EU Dev (eu-central-1, eu-dev stage) — for dev-* hostname prefixes */
const EU_DEV_ENDPOINTS: EuEndpoints = {
  apiUrl: 'https://eu-dev.api.synopsis.rozie.ai',
  wsUrl: 'wss://ws.eu-dev.api.synopsis.rozie.ai',
  xApiKey: 'Yqa7AKeRCp7ttQxHeeNa32327Hnv9Llw9LtgE8of',
};

/** EU Prod (eu-central-1, eu-prod stage) — for prod hostname prefixes */
const EU_PROD_ENDPOINTS: EuEndpoints = {
  apiUrl: 'https://eu.api.synopsis.rozie.ai',
  wsUrl: 'wss://ws.eu.api.synopsis.rozie.ai',
  xApiKey: 'fQkA6abZqr9WtLBbIziVD4DcVjFzd24g7y7bZbly',
};

export type EuStage = 'dev' | 'prod' | null;

/** Event id from a host/domain label, with a leading 'dev-' stripped. */
export function euEventId(hostname: string): string {
  const label = hostname.split('.')[0]?.toLowerCase() ?? '';
  return label.startsWith('dev-') ? label.slice(4) : label;
}

/** Check if the event (subdomain host or ?event= domain) is hosted in EU. */
export function isEuEvent(hostname: string): boolean {
  return EU_EVENT_IDS.has(euEventId(hostname));
}

/**
 * EU endpoints for the event, or null. The stage comes from the build
 * environment, never from the label (dev build -> eu-dev, prod build -> eu-prod).
 */
export function getEuEndpointsForHostname(
  hostname: string,
  stage: EuStage
): EuEndpoints | null {
  if (!stage || !isEuEvent(hostname)) return null;
  return stage === 'dev' ? EU_DEV_ENDPOINTS : EU_PROD_ENDPOINTS;
}
